import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Upload, ExternalLink, FileStack } from 'lucide-react';
import api from '../utils/api';
import { CLOSE_LABELS } from '../constants/bwofWorkflow';
import BuildingDataPackModal from './BuildingDataPackModal';
import { InfoButton } from './InfoModal';
import { formatPackWhen, packOutcomeLabel, packReceiptLabel } from '../utils/dataPackLog';

function isFullAgent(building) {
  const scope = String(building?.agency_scope || '').toUpperCase();
  if (scope === 'FULL_AGENT') return true;
  if (scope === 'SS_ONLY') return false;
  return !!building?.is_bwof_agent;
}

const CLOSE_ORDER = ['pack_prep', 'paid', 'lodged', 'under_review', 'approved'];

/**
 * Building Paperwork → Council / Agency.
 * Full agent: pack + lodge path. Partial / Main agent: gather & send + upload returns.
 * Does not replace Building Overview Close progress — links to it.
 */
export default function BuildingPaperworkCouncilTab({
  buildingId,
  building,
}) {
  const queryClient = useQueryClient();
  const [dataPackOpen, setDataPackOpen] = useState(false);
  const docsBase = `/buildings/${buildingId}/documents`;
  const full = isFullAgent(building);

  const { data: packLogs, isLoading: logsLoading } = useQuery({
    queryKey: ['data-pack-logs', buildingId],
    queryFn: async () =>
      (await api.get(`/buildings/${buildingId}/data-pack/logs`)).data?.data || [],
    enabled: !!buildingId,
  });

  const { data: wf } = useQuery({
    queryKey: ['bwof-workflow', buildingId],
    queryFn: async () => (await api.get(`/bwof-workflow/buildings/${buildingId}`)).data?.data,
    enabled: !!buildingId,
  });

  const closeStep = wf?.close_step || wf?.close?.step || 'pack_prep';

  return (
    <div className="space-y-4">
      {full && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
          <p className="font-semibold">Full BWOF agent</p>
          <p className="mt-1 text-sm opacity-90">
            Prepare the council pack, track fees / lodge / review, and upload council reply documents when they arrive.
          </p>
        </div>
      )}

      <div className="rounded-xl border bg-white p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 inline-flex items-center gap-2">
            Building data pack
            <InfoButton title="External Agent hand-off">
              {building?.partly_agent || building?.partly_agent_id ? (
                <div>
                  <p className="font-semibold text-gray-900">
                    {building.partly_agent?.name || 'Linked External Agent'}
                  </p>
                  <p className="mt-0.5">
                    {[
                      building.partly_agent?.contact_name,
                      building.partly_agent?.email,
                      building.partly_agent?.phone,
                      building.partly_agent?.address,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No contact details on file'}
                  </p>
                  <Link
                    to={`/buildings/${buildingId}/edit`}
                    className="text-xs font-semibold text-primary-700 hover:underline mt-1 inline-block"
                  >
                    Edit on building form
                  </Link>
                </div>
              ) : (
                <p>
                  No External Agent set for this building.{' '}
                  <Link to={`/buildings/${buildingId}/edit`} className="font-semibold underline">
                    Set on Edit Building
                  </Link>
                  {' · '}
                  <Link to="/network?tab=partly-agent" className="font-semibold underline">
                    External Agent list
                  </Link>
                </p>
              )}
              <ol className="list-decimal list-inside space-y-1">
                <li>Issue SS forms (SS forms tab) or register external paperwork.</li>
                <li>Prepare pack above, then open Documents to attach / download.</li>
                <li>Send pack or individual issued docs from Issued docs tab.</li>
                <li>Upload any reply from Main agent or council into Documents.</li>
              </ol>
            </InfoButton>
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            CS + floor drawings + recent inspections + optional external reports → one PDF (save and/or email).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDataPackOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50"
        >
          <FileStack className="w-4 h-4" />
          Send data pack
        </button>
      </div>

      {dataPackOpen ? (
        <BuildingDataPackModal
          buildingId={buildingId}
          buildingName={building?.name}
          onClose={() => {
            setDataPackOpen(false);
            queryClient.invalidateQueries({ queryKey: ['data-pack-logs', buildingId] });
          }}
        />
      ) : null}

      <div className="rounded-xl border bg-white">
        <div className="px-4 py-3 border-b">
          <p className="font-semibold text-gray-900">Pack log</p>
          <p className="text-xs text-gray-500 mt-0.5">
            When, to whom, what, why, who, and whether it was received and opened.
          </p>
        </div>
        {logsLoading && <p className="px-4 py-3 text-sm text-gray-500">Loading…</p>}
        {!logsLoading && !(packLogs || []).length && (
          <p className="px-4 py-3 text-sm text-gray-500">No packs saved or sent yet.</p>
        )}
        <ul>
          {(packLogs || []).map((row) => (
            <li key={row.id} className="border-t first:border-t-0">
              <Link
                to={`/buildings/${buildingId}/paperwork/pack-logs/${row.id}`}
                className="block px-4 py-3 text-sm text-gray-800 hover:bg-slate-50"
              >
                <span className="font-medium">{formatPackWhen(row.created_at)}</span>
                {' · '}
                {row.recipient_email || '—'}
                {' · '}
                {row.what_text || 'Building data pack'}
                {' · '}
                {row.why_text || '—'}
                {' · '}
                {row.actor_name || '—'}
                {' · '}
                {packOutcomeLabel(row.outcome)}
                {' · '}
                {packReceiptLabel(row)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      {full && (
        <div className="rounded-xl border bg-white p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold text-gray-900">Close steps (council)</p>
            <Link
              to={`/buildings/${buildingId}#ss-progress`}
              className="text-xs font-semibold text-primary-700 hover:underline inline-flex items-center gap-1"
            >
              Open building progress <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {CLOSE_ORDER.map((key) => {
              const idx = CLOSE_ORDER.indexOf(key);
              const cur = CLOSE_ORDER.indexOf(closeStep);
              const done = cur > idx || closeStep === 'approved';
              const active = key === closeStep;
              return (
                <span
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${
                    active
                      ? 'border-primary-500 bg-primary-50 font-semibold text-primary-900'
                      : done
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-gray-200 bg-white text-gray-500'
                  }`}
                >
                  {CLOSE_LABELS[key] || key}
                </span>
              );
            })}
          </div>
          <p className="text-xs text-gray-500">
            Advance Close steps on Building Overview. This tab is for pack files and document
            exchange.
          </p>
        </div>
      )}

      <div className="rounded-xl border bg-white p-4 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <p className="font-semibold text-gray-900 inline-flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload / documents cabinet
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            Store council reply, Main-agent returns, fee receipts, and pack PDFs for this building.
          </p>
        </div>
        <Link
          to={docsBase}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-white text-sm font-semibold text-gray-800 hover:bg-slate-50"
        >
          Open Documents
        </Link>
      </div>
    </div>
  );
}
